import { CardSubWrapper, CardWrapper } from 'admin/components/wrappers';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import { AdminTextField } from 'app/components/AdminTextField';
import AdminCustomButton from 'app/components/AdminCustomButton';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { GroupedPaymentPlans, PlanAmountUpdate } from 'services/openapi';
import { usePermission } from 'hooks/usePermission';
import { extractFeaturesForPlan } from 'utils/common';

export interface FeeDataItem {
    id: string;
    name: string;
    amount: number;
    convenienceFee: number;
    features: string[];
    planIds: string[]; // Store individual plan IDs for save payload
}
export const planIdsToRemove = [
    "89bb7e85-0dce-4a55-95cc-48dcdfe78086",
    "0440472d-a6cf-4530-a9ea-4ee7cadae39c",
    "33e17418-a6d7-49ea-ac5b-621155bb58cd",
    "2fb60026-14ca-4980-9c93-8f9d54488591",
    "b1ce86aa-a22c-4424-9eb3-f30ad5a00546"
];
interface Props {
    paymentPlansList: GroupedPaymentPlans[];
    updatePaymentPlan: (
        requestBody?: PlanAmountUpdate,
        onSuccess?: () => void,
    ) => void;
    getPaymentPlansList: () => void;
    searchTerm?: string;
}

// Combined input cell component with local state for both fields
const FeeInputCell = ({
    dataItem,
    onSave,
}: {
    dataItem: FeeDataItem;
    onSave: (id: string, amount: number, convenienceFee: number) => void;
}) => {
    const { hasViewPermission } = usePermission();
    const initialAmount = dataItem.amount?.toString() || '0';
    const initialConvenienceFee = dataItem.convenienceFee?.toString() || '0';

    const [amountData, setAmountData] = React.useState<string>(initialAmount);
    const [convenienceFeeData, setConvenienceFeeData] = React.useState<string>(
        initialConvenienceFee,
    );

    // Reset local state when dataItem changes
    React.useEffect(() => {
        setAmountData(dataItem.amount?.toString() || '0');
        setConvenienceFeeData(dataItem.convenienceFee?.toString() || '0');
    }, [dataItem.amount, dataItem.convenienceFee]);

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        if (inputValue.length <= 4 && /^\d*$/.test(inputValue)) {
            setAmountData(inputValue);
        }
    };

    const handleConvenienceFeeChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const inputValue = e.target.value;

        if (inputValue.length <= 10 && /^\d*$/.test(inputValue)) {
            const numericValue = parseFloat(inputValue || '0');
            const amountValue = parseFloat(amountData || '0');

            if (numericValue <= amountValue) {
                setConvenienceFeeData(inputValue);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (['e', 'E', '+', '-'].includes(e.key)) {
            e.preventDefault();
        }
    };

    // Check if either field has changed
    const hasAmountChanged = amountData !== initialAmount;
    const hasConvenienceFeeChanged = convenienceFeeData !== initialConvenienceFee;
    const hasAnyChanges = hasAmountChanged || hasConvenienceFeeChanged;

    return (
        <td style={{ padding: '8px' }} colSpan={3}>
            <CombinedInputWrapper>
                <InputRow>
                    <InputGroup>
                        <AdminTextField
                            onChange={handleAmountChange}
                            value={amountData}
                            type="text"
                            onKeyDown={handleKeyDown}
                            style={{ width: '100%' }}
                            disabled={!hasViewPermission('0900101013')}
                        />
                    </InputGroup>

                    <InputGroup>
                        <AdminTextField
                            onChange={handleConvenienceFeeChange}
                            value={convenienceFeeData}
                            type="text"
                            onKeyDown={handleKeyDown}
                            style={{ width: '100%' }}
                            className="AdminTextField"
                            disabled={!hasViewPermission('0900101013')}
                        />
                    </InputGroup>

                    <SaveButtonGroup>
                        {hasViewPermission('0900101013') && (
                            <AdminCustomButton
                                buttonType="save"
                                onClick={() => {
                                    onSave(
                                        dataItem.id,
                                        parseFloat(amountData) || 0,
                                        parseFloat(convenienceFeeData) || 0,
                                    );
                                }}
                                disabled={
                                    !hasAnyChanges ||
                                    amountData === '' ||
                                    convenienceFeeData === ''
                                }
                            >
                                Save
                            </AdminCustomButton>
                        )}
                    </SaveButtonGroup>
                </InputRow>
            </CombinedInputWrapper>
        </td>
    );
};

const FeeManagementTable = ({
    paymentPlansList,
    updatePaymentPlan,
    getPaymentPlansList,
    searchTerm,
}: Props) => {
    const { hasViewPermission } = usePermission();

    // Transform the payment plans data - Each plan as individual row
    const transformedData = React.useMemo(() => {
        const result: FeeDataItem[] = [];
        let groupIdCounter = 1;

        paymentPlansList?.forEach(group => {
            if (!group || !group.plans) return;

            group.plans.forEach(plan => {
                // Filter out plans with IDs in the removal list
                if (!plan?.id || planIdsToRemove.includes(plan.id)) {
                    return;
                }

                if (plan && plan.type?.name) {
                    // Extract features for this individual plan
                    const features = extractFeaturesForPlan(plan);
                    let displayName: string;

                    if (features.length > 0) {
                        // If features exist, show plan type with features
                        displayName = `${plan.type.name} (${features.join(', ')})`;
                    } else {
                        // If no features are true, show just the plan type name
                        displayName = plan.type.name;
                    }

                    result.push({
                        id: `${groupIdCounter++}`,
                        name: displayName,
                        amount: plan.amount || 0,
                        convenienceFee: plan.convenienceFee || 0,
                        features: features,
                        planIds: [plan.id],
                    });
                }
            });
        });

        return result;
    }, [paymentPlansList]);
    // 🔎 Filter transformed data by "Plan Type" (name)
    const filteredData = React.useMemo(() => {
        if (!searchTerm?.trim()) return transformedData;
        return transformedData.filter(item =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [transformedData, searchTerm]);

    const [feeData, setFeeData] = React.useState<FeeDataItem[]>(transformedData);

    // Update feeData when transformedData changes
    React.useEffect(() => {
        setFeeData(filteredData);
    }, [filteredData]);

    const handleSave = React.useCallback(
        (id: string, newAmount: number, newConvenienceFee: number) => {
            const currentRow = feeData.find(item => item.id === id);

            // Create save payload for the current row's plan with both fields
            const savePayload: PlanAmountUpdate = {
                id: currentRow?.planIds[0] || '', // Get the first (and only) plan ID
                amount: newAmount,
                convenienceFee: newConvenienceFee,
            };

            // Update local state with both values
            setFeeData(prev =>
                prev.map(item =>
                    item.id === id
                        ? { ...item, amount: newAmount, convenienceFee: newConvenienceFee }
                        : item,
                ),
            );

            console.log('Save payload:', savePayload);
            updatePaymentPlan(savePayload, () => {
                setTimeout(() => {
                    getPaymentPlansList();
                }, 300); // small delay so backend finishes update
            });
        },
        [feeData, updatePaymentPlan, getPaymentPlansList],
    );

    const FeeColumns = [
        {
            field: 'name',
            title: 'Plan Type',
            width: '350px',
            cell: (e: any) => (
                <td style={{ padding: '8px' }}>
                    <span>{e.dataItem.name}</span>
                </td>
            ),
        },
        {
            field: 'combined',
            title: '', // Empty title since we'll use a custom header
            headerCell: () => (
                <HeaderRow>
                    <HeaderGroup>Amount</HeaderGroup>
                    <HeaderGroup>Convenience Fee</HeaderGroup>
                    {hasViewPermission('0900101013') && <HeaderGroup>Action</HeaderGroup>}
                </HeaderRow>
            ),
            cell: (e: any) => (
                <FeeInputCell dataItem={e.dataItem} onSave={handleSave} />
            ),
        },
    ];

    return (
        <CardWrapper style={{ padding: '15px 0 50px' }}>
            <CardSubWrapper className="d-flex"></CardSubWrapper>
            <GridWrapper>
                <CustomDataGrid data={feeData} columns={FeeColumns} />
            </GridWrapper>
        </CardWrapper>
    );
};

export default FeeManagementTable;
const GridWrapper = styled.div`
  .k-grid-header tr th:nth-last-child(1) {
    padding: 13px 24px 13px 10px;
  }
  .k-grid-content tr {
    &:hover {
      td {
        background: #f0f0f0 !important;
      }
    }
  }
  .k-grid-content tr td {
    cursor: pointer;
    .label {
      display: none;
    }
    input {
      font-family: 'Inter';
      font-weight: 500;
      font-size: 12px;
      line-height: 14.52px;
      max-width: 100%;
    }
    div {
      font-family: 'Inter';
      font-weight: 500;
      font-size: 12px;
      line-height: 14.52px;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      align-items: center;
      width: 100%;
    }
    .custom-cell-style {
      width: fit-content;
      max-width: 100%;
      cursor: pointer;
    }
  }
  .k-grid-content tr td.team-cell {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 200px;
  }
  input:disabled {
    opacity: 1;
    cursor: pointer;
    padding: 0px !important;
    border-width: 0px;
    border-color: transparent !important;
    background-color: transparent !important;
    max-width: unset;
    width: fit-content;
  }
`;
const CombinedInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 16px;
  align-items: flex-end;
  width: 100%;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const SaveButtonGroup = styled.div`
  display: flex;
  align-items: flex-end;
  padding-bottom: 2px;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 16px;
  width: calc(100% - 15px);
`;

const HeaderGroup = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #475467;
  font-family: 'Inter';
`;
